/**
 * Article Gallery Component
 * Custom gallery implementation with thumbnails for blog articles
 */

class ArticleGallery extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.images = [];
    this.thumbnailsPerView = 5;
  }

  connectedCallback() {
    this.collectImages();

    if (this.images.length < 2) {
      console.log('[ArticleGallery] Not enough images for gallery, displaying as static');
      return;
    }

    this.render();
    this.setupEventListeners();

    console.log(`[ArticleGallery] Initialized with ${this.images.length} images`);
  }

  collectImages() {
    // Collect all img elements from the original swiper
    const slides = this.querySelectorAll('.swiper-slide');

    slides.forEach(slide => {
      const img = slide.querySelector('img');
      const anchor = slide.querySelector('a');

      if (img) {
        this.images.push({
          src: img.src,
          alt: img.alt || '',
          link: anchor ? anchor.href : null
        });
      }
    });
  }

  render() {
    this.innerHTML = `
      <div class="article-gallery-container">
        <div class="article-gallery-main">
          <button class="article-gallery-nav article-gallery-prev" aria-label="Previous image">
            <svg xmlns="http://www.w3.org/2000/svg" width="21" height="35" viewBox="0 0 21 35" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M20.1431 2.2981L4.68473 17.4648L20.1431 32.6314L17.8008 34.9295L0.000149768 17.4648L17.8008 -1.02385e-07L20.1431 2.2981Z" fill="white"></path>
            </svg>
          </button>

          <div class="article-gallery-slides">
            ${this.images.map((img, index) => `
              <div class="article-gallery-slide ${index === 0 ? 'active' : ''}" data-index="${index}">
                ${img.link ? `<a href="${img.link}" target="_blank">` : ''}
                  <img src="${img.src}" alt="${img.alt}" loading="${index === 0 ? 'eager' : 'lazy'}">
                ${img.link ? `</a>` : ''}
              </div>
            `).join('')}
          </div>

          <button class="article-gallery-nav article-gallery-next" aria-label="Next image">
            <svg xmlns="http://www.w3.org/2000/svg" width="21" height="35" viewBox="0 0 21 35" fill="none" style="transform: rotate(180deg);">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M20.1431 2.2981L4.68473 17.4648L20.1431 32.6314L17.8008 34.9295L0.000149768 17.4648L17.8008 -1.02385e-07L20.1431 2.2981Z" fill="white"></path>
            </svg>
          </button>

          <div class="article-gallery-counter">
            <span class="current">1</span> / <span class="total">${this.images.length}</span>
          </div>
        </div>

        <div class="article-gallery-thumbnails">
          <div class="article-gallery-thumbnails-track">
            ${this.images.map((img, index) => `
              <button
                class="article-gallery-thumbnail ${index === 0 ? 'active' : ''}"
                data-index="${index}"
                aria-label="Go to image ${index + 1}"
              >
                <img src="${img.src}" alt="${img.alt}">
              </button>
            `).join('')}
          </div>
        </div>
      </div>
    `;

    // Set initial height after render
    setTimeout(() => this.updateContainerHeight(), 0);
  }

  setupEventListeners() {
    // Navigation buttons
    this.querySelector('.article-gallery-prev').addEventListener('click', () => this.prev());
    this.querySelector('.article-gallery-next').addEventListener('click', () => this.next());

    // Thumbnail clicks
    this.querySelectorAll('.article-gallery-thumbnail').forEach(thumb => {
      thumb.addEventListener('click', (e) => {
        const index = parseInt(e.currentTarget.dataset.index);
        this.goToSlide(index);
      });
    });

    // Keyboard navigation
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.prev();
      if (e.key === 'ArrowRight') this.next();
    });

    // Listen for image load events to recalculate height
    this.querySelectorAll('.article-gallery-slide img').forEach(img => {
      if (img.complete) {
        // Image already loaded
        this.updateContainerHeight();
      } else {
        img.addEventListener('load', () => this.updateContainerHeight());
      }
    });

    // Optional: Touch swipe support
    this.setupTouchEvents();
  }

  setupTouchEvents() {
    const slidesContainer = this.querySelector('.article-gallery-slides');
    let touchStartX = 0;
    let touchEndX = 0;

    slidesContainer.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    slidesContainer.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    }, { passive: true });

    this.handleSwipe = () => {
      const swipeThreshold = 50;
      const diff = touchStartX - touchEndX;

      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          this.next();
        } else {
          this.prev();
        }
      }
    };
  }

  prev() {
    // Loop to last if at beginning
    const newIndex = this.currentIndex === 0
      ? this.images.length - 1
      : this.currentIndex - 1;

    this.goToSlide(newIndex);
  }

  next() {
    // Loop to first if at end
    const newIndex = this.currentIndex === this.images.length - 1
      ? 0
      : this.currentIndex + 1;

    this.goToSlide(newIndex);
  }

  goToSlide(index) {
    // Update current index
    this.currentIndex = index;

    // Update slides
    const slides = this.querySelectorAll('.article-gallery-slide');
    slides.forEach((slide, i) => {
      slide.classList.toggle('active', i === index);
    });

    // Update thumbnails
    const thumbnails = this.querySelectorAll('.article-gallery-thumbnail');
    thumbnails.forEach((thumb, i) => {
      thumb.classList.toggle('active', i === index);
    });

    // Update counter
    this.querySelector('.article-gallery-counter .current').textContent = index + 1;

    // Scroll thumbnails to show active
    this.scrollThumbnailsToActive();

    // Update container height to match active slide
    this.updateContainerHeight();
  }

  updateContainerHeight() {
    const activeSlide = this.querySelector('.article-gallery-slide.active');
    const slidesContainer = this.querySelector('.article-gallery-slides');

    if (!activeSlide || !slidesContainer) return;

    const activeImg = activeSlide.querySelector('img');
    if (!activeImg) return;

    // Get the natural dimensions of the active image
    const imgHeight = activeImg.offsetHeight;

    if (imgHeight > 0) {
      // Set the container height to match the active image
      slidesContainer.style.height = `${imgHeight}px`;
    }
  }

  scrollThumbnailsToActive() {
    const track = this.querySelector('.article-gallery-thumbnails-track');
    const activeThumbnail = this.querySelector('.article-gallery-thumbnail.active');

    if (!track || !activeThumbnail) return;

    const trackWidth = track.offsetWidth;
    const thumbWidth = activeThumbnail.offsetWidth;
    const thumbLeft = activeThumbnail.offsetLeft;

    // Calculate if we need to scroll
    const visibleStart = track.scrollLeft;
    const visibleEnd = visibleStart + trackWidth;
    const thumbEnd = thumbLeft + thumbWidth;

    // Scroll to center the active thumbnail
    if (thumbLeft < visibleStart || thumbEnd > visibleEnd) {
      const scrollTo = thumbLeft - (trackWidth / 2) + (thumbWidth / 2);
      track.scrollTo({
        left: scrollTo,
        behavior: 'smooth'
      });
    }
  }
}

// Define the custom element
if (!customElements.get('article-gallery')) {
  customElements.define('article-gallery', ArticleGallery);
}

// Initialize galleries on page load
document.addEventListener('DOMContentLoaded', () => {
  injectGalleryFromMetafield();
  initializeArticleGalleries();
});

// Also initialize on article load for infinite scroll
document.addEventListener('article:load', () => {
  setTimeout(() => {
    injectGalleryFromMetafield();
    initializeArticleGalleries();
  }, 100);
});

/**
 * Inject galleries from metafield data
 * Supports multiple galleries with correct ordering based on placeholder type
 */
function injectGalleryFromMetafield() {
  // Process all unprocessed article bodies (supports infinite scroll)
  const articleBodies = document.querySelectorAll('.main-article.wysiwyg:not([data-galleries-injected])');
  articleBodies.forEach(articleBody => injectGalleriesInArticle(articleBody));
}

function injectGalleriesInArticle(articleBody) {
  articleBody.dataset.galleriesInjected = 'true';

  // Get gallery data scoped to this article body
  const multiScript = articleBody.querySelector('script[data-article-galleries]');
  const legacyScript = articleBody.querySelector('script[data-article-gallery-images]');

  let newGalleries = [];
  let legacyGallery = null;

  if (multiScript) {
    try { newGalleries = JSON.parse(multiScript.textContent); } catch (e) {}
  }
  if (legacyScript) {
    try { legacyGallery = JSON.parse(legacyScript.textContent); } catch (e) {}
  }

  // Define placeholder patterns by type
  const isNewPlaceholder = (text) => text.trim() === 'GALLERY-EMBED';
  const isLegacyPlaceholder = (text) => {
    const t = text.trim();
    return ['<- Display Slideshow ->', '&lt;- Display Slideshow -&gt;', '<- Gallery ->', '&lt;- Gallery -&gt;'].includes(t);
  };
  const isPlaceholder = (text) => isNewPlaceholder(text) || isLegacyPlaceholder(text);

  // Find all placeholder text nodes in DOM order
  const placeholders = [];
  const walker = document.createTreeWalker(articleBody, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    const text = walker.currentNode.textContent;
    if (isPlaceholder(text)) {
      placeholders.push({
        node: walker.currentNode,
        isNew: isNewPlaceholder(text),
        isLegacy: isLegacyPlaceholder(text)
      });
    }
  }

  // Use separate counters for each type
  let newGalleryIndex = 0;
  let legacyUsed = false;

  // Process each placeholder in DOM order
  placeholders.forEach((placeholder) => {
    const { node: placeholderElement, isNew, isLegacy } = placeholder;
    const parent = placeholderElement.parentNode;
    const isOnlyPlaceholder = isPlaceholder(placeholderElement.textContent.trim());

    // Determine which gallery images to use based on placeholder type
    let galleryImages = null;
    if (isNew && newGalleryIndex < newGalleries.length) {
      galleryImages = newGalleries[newGalleryIndex];
      newGalleryIndex++;
    } else if (isLegacy && legacyGallery && !legacyUsed) {
      galleryImages = legacyGallery;
      legacyUsed = true;
    }

    if (galleryImages && galleryImages.length > 0) {
      // Build gallery element
      const swiperContainer = document.createElement('swiper-container');
      swiperContainer.setAttribute('init', 'false');
      swiperContainer.setAttribute('data-desktop-layout', 'slider');
      swiperContainer.setAttribute('data-mobile-layout', 'slider');
      swiperContainer.setAttribute('watch-slides-progress', 'true');
      swiperContainer.setAttribute('navigation', 'true');
      swiperContainer.setAttribute('pagination', 'false');
      swiperContainer.setAttribute('effect', 'slide');
      swiperContainer.setAttribute('watch-overflow', 'true');
      swiperContainer.setAttribute('data-mobile-slides-per-view', '1');
      swiperContainer.setAttribute('data-tablet-slides-per-view', '1');
      swiperContainer.setAttribute('data-desktop-slides-per-view', '1');
      swiperContainer.setAttribute('data-space-between-mobile', '16');
      swiperContainer.setAttribute('data-space-between-tablet', '32');
      swiperContainer.setAttribute('data-space-between-desktop', '32');
      swiperContainer.classList.add('article-gallery');

      galleryImages.forEach((imageUrl, index) => {
        const slide = document.createElement('div');
        slide.className = 'swiper-slide';
        slide.innerHTML = `<img src="${imageUrl}" alt="Gallery image ${index + 1}" loading="${index === 0 ? 'eager' : 'lazy'}">`;
        swiperContainer.appendChild(slide);
      });

      // Replace placeholder with gallery
      if (isOnlyPlaceholder && parent.tagName === 'P') {
        parent.parentNode.replaceChild(swiperContainer, parent);
      } else {
        parent.replaceChild(swiperContainer, placeholderElement);
      }
    } else {
      // No gallery for this placeholder - remove it
      if (isOnlyPlaceholder && parent.tagName === 'P') {
        parent.parentNode.removeChild(parent);
      } else {
        parent.removeChild(placeholderElement);
      }
    }
  });
}

function initializeArticleGalleries() {
  console.log('[ArticleGallery] Initializing article galleries');

  // Find all swiper-container.article-gallery elements
  const swiperGalleries = document.querySelectorAll('swiper-container.article-gallery:not([data-converted])');
  console.log(`[ArticleGallery] Found ${swiperGalleries.length} galleries to convert`);

  swiperGalleries.forEach((swiperGallery, index) => {
    // Mark as converted
    swiperGallery.dataset.converted = 'true';

    // Create new article-gallery element
    const articleGallery = document.createElement('article-gallery');

    // Copy all slides from swiper to new gallery
    const slides = swiperGallery.querySelectorAll('.swiper-slide');
    slides.forEach(slide => {
      articleGallery.appendChild(slide.cloneNode(true));
    });

    // Replace swiper with custom gallery
    swiperGallery.parentNode.replaceChild(articleGallery, swiperGallery);

    console.log(`[ArticleGallery] Converted gallery ${index + 1}`);
  });
}
